import { useState, useEffect, useRef } from "react";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { safeFetch } from "@/api/safeFetch";
import { buildImageUrl } from "@/api/buildImageUrl";

type CarruselSource = "api" | "static";


interface ImagenTrabajo {
  imagen_trabajo_id: number;
  imagen_trabajo_realizado: string;
}

interface CarruselImgTrabajosProps {
  source?: CarruselSource;
  profesionalId?: number;
  showNoImagesText?: boolean;
  images?: string[] | ImagenTrabajo[];

  // ⭐ PRO FEATURES
  autoPlay?: boolean;
  autoPlayInterval?: number;
  enableZoom?: boolean;

  lightbox?: boolean;
}

const CarruselImgTrabajos: React.FC<CarruselImgTrabajosProps> = ({
  profesionalId,
  images: imagesProp,
  showNoImagesText = true,
  autoPlay = false,
  autoPlayInterval = 4000,
  enableZoom = true,
  lightbox = false,
}) => {
  const [images, setImages] = useState<ImagenTrabajo[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const toggleFullScreen = () => setIsFullScreen((p) => !p);

  // 🔥 NORMALIZADOR
  const normalizeImages = (
    imgs: string[] | ImagenTrabajo[] = []
  ): ImagenTrabajo[] =>
    imgs.map((img, i) =>
      typeof img === "string"
        ? {
          imagen_trabajo_id: i,
          imagen_trabajo_realizado: img,
        }
        : img
    );

  // 🔥 FETCH + PROPS
  useEffect(() => {
    setLoading(true);

    if (imagesProp) {
      setImages(normalizeImages(imagesProp));
      setLoading(false);
      return;
    }

    if (!profesionalId) {
      setLoading(false);
      return;
    }

    const fetchImages = async () => {
      try {
        const res = await safeFetch(
          `/api/imagenesTrabajos/${profesionalId}`
        );

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.message || "Error al obtener imágenes");
        }

        setImages(data || []);
      } catch (err: any) {
        setError(err.message || "Error desconocido");
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [profesionalId, imagesProp]);

  // 🔥 LOADING SKELETON
  if (loading) {
    return (
      <div className="w-full h-96 bg-zinc-800 animate-pulse rounded-2xl" />
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-center mt-4">
        {error}
      </div>
    );
  }

  if (!images.length) {
    return showNoImagesText ? (
      <div className="text-center text-zinc-500 py-2">
        No hay imágenes disponibles.
      </div>
    ) : null;
  }

  return (
    <>
      {/* 🔥 FULLSCREEN / ZOOM LIGHTBOX */}
      {isFullScreen && enableZoom && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/95 z-50"
          onClick={toggleFullScreen}
        >
          {images.map((img, i) => (
            <div key={i} className="h-96 bg-black">
              <img
                src={buildImageUrl(img.imagen_trabajo_realizado)}
                loading="lazy"
                className="w-full h-full object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).src =
                    "/img/logo/ImagenLogo.png";
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* CAROUSEL */}
      <div
        ref={containerRef}
        className="grid grid-cols-12 gap-4 select-none"
      >
        <div className="col-span-12 sm:col-span-8 relative">
          <Carousel
            showArrows
            showThumbs={false}
            infiniteLoop
            useKeyboardArrows
            showStatus={false}
            selectedItem={currentSlide}
            onChange={setCurrentSlide}
            autoPlay={autoPlay}
            interval={autoPlayInterval}
            swipeable={true}
            emulateTouch={true}
            dynamicHeight={false}
            className="w-full rounded-3xl overflow-hidden shadow-2xl"
          >
            {images.map((img, i) => (
              <div key={i} className="h-96 bg-black">
                {/* 🔥 lazy loading real */}
                <img
                  src={buildImageUrl(img.imagen_trabajo_realizado)}
                  loading="lazy"
                  className="w-full h-full object-contain"
                  onClick={() => enableZoom && toggleFullScreen()}
                />
              </div>
            ))}
          </Carousel>

          <div className="absolute top-2 right-2 bg-black/60 text-white px-2 py-1 rounded-md text-xs">
            {currentSlide + 1} / {images.length}
          </div>
        </div>

      
        {/* THUMBNAILS */}
        {!lightbox && (
          <div className="col-span-12 sm:col-span-4 h-36 sm:h-96 flex flex-wrap gap-4 justify-center items-center overflow-y-auto p-1">
            {images.map((img, index) => (
              <div
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`cursor-pointer rounded-xl overflow-hidden h-16 sm:h-20 border-2 transition-all duration-300 ${index === currentSlide
                    ? "border-cyan-500 scale-110"
                    : "border-zinc-700"
                  }`}
              >
                <img
                  src={buildImageUrl(img.imagen_trabajo_realizado)}
                  loading="lazy"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-cover"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default CarruselImgTrabajos;